import { useMemo } from 'react';
import { Lead } from '../types/Lead';
import { useScoring } from '../contexts/ScoringContext';

/**
 * Custom hook for sorting leads by their custom weighted scores
 * @param leads - Array of leads to sort
 * @returns Sorted leads array (descending order by custom score)
 */
export const useSortedLeads = (leads: readonly Lead[]): Lead[] => {
  const { calculateCustomScore, weights } = useScoring();

  return useMemo(() => {
    // Handle empty or invalid leads array
    if (!leads || leads.length === 0) {
      return [];
    }

    try {
      return [...leads].sort((a, b) => {
        // Safety check for required properties
        if (!a || !b || !a.processed || !b.processed) {
          return 0;
        }

        const scoreA = calculateCustomScore(a);
        const scoreB = calculateCustomScore(b);
        return scoreB - scoreA; // Sort in descending order (highest scores first)
      });
    } catch (error) {
      console.error('Error sorting leads:', error);
      // Return original array if sorting fails
      return [...leads];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, calculateCustomScore, weights]);
};

export default useSortedLeads;